import React, { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { MapPin, Phone, Mail, Clock, Send, Truck, RotateCcw, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SHOP_EMAIL = "shop-2026@outlook.fr";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    setSending(true);
    const body = `Nom : ${form.name}%0D%0AEmail : ${form.email}%0D%0A%0D%0A${form.message}`;
    window.location.href = `mailto:${SHOP_EMAIL}?subject=${encodeURIComponent("Contact - shop-canapé")}&body=${encodeURIComponent(form.message ? "" : "")}${body}`;
    setTimeout(() => { setSending(false); toast.success("Votre messagerie s'ouvre pour nous écrire."); }, 400);
  };

  const infos = [
    { icon: MapPin, title: "Showroom", value: "24 rue du Faubourg, 75008 Paris" },
    { icon: Phone, title: "Téléphone", value: "+33 1 23 45 67 89" },
    { icon: Mail, title: "Email", value: SHOP_EMAIL },
    { icon: Clock, title: "Horaires", value: "Lun–Sam · 10h – 19h" },
  ];

  const policies = [
    { icon: Truck, title: "Livraison", desc: "Livraison gant blanc offerte dès 1500€, sous 3 à 5 semaines, avec installation." },
    { icon: RotateCcw, title: "Retours", desc: "30 jours pour changer d'avis. Retour gratuit et remboursement intégral." },
    { icon: ShieldCheck, title: "Paiement", desc: "Paiement 100% sécurisé. Confirmation de commande par email." },
  ];

  return (
    <div className="pt-6">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="py-14 text-center">
          <p className="mb-3 font-accent text-xs uppercase tracking-[0.25em] text-gold-dark">Contact</p>
          <h1 className="font-serif text-4xl font-medium lg:text-6xl">Parlons de votre projet</h1>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">Une question, un conseil déco ou un projet sur mesure ? Notre équipe vous répond avec plaisir.</p>
        </Reveal>

        <div className="grid gap-10 pb-16 lg:grid-cols-[1fr_400px]">
          <Reveal>
            <form onSubmit={submit} className="rounded-3xl border border-border bg-card p-8 shadow-soft" data-testid="contact-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Nom</label>
                  <input required value={form.name} onChange={set("name")} data-testid="contact-name" className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-gold" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Email</label>
                  <input type="email" required value={form.email} onChange={set("email")} data-testid="contact-email" className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-gold" />
                </div>
              </div>
              <div className="mt-4">
                <label className="mb-1.5 block text-sm font-medium">Message</label>
                <textarea required rows={6} value={form.message} onChange={set("message")} data-testid="contact-message" className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-gold" />
              </div>
              <button type="submit" disabled={sending} data-testid="contact-submit" className="mt-6 flex items-center gap-2 rounded-full bg-obsidian px-8 py-3.5 text-sm font-semibold text-cream transition-colors hover:bg-gold hover:text-obsidian disabled:opacity-60">
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Envoyer le message
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.1} className="space-y-4">
            {infos.map((info) => (
              <div key={info.title} className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5 shadow-soft">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-gold-dark"><info.icon size={19} /></div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">{info.title}</p>
                  <p className="font-medium">{info.value}</p>
                </div>
              </div>
            ))}
          </Reveal>
        </div>

        <div className="grid gap-5 pb-20 md:grid-cols-3">
          {policies.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="h-full rounded-3xl border border-border bg-card p-7 shadow-soft">
                <p.icon size={22} className="mb-4 text-gold-dark" />
                <h3 className="font-serif text-xl">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
